/**
 * Base width cột cố định trên lưới list (px).
 * Checkbox / STT / trạng thái / ngày / thao tác — vừa đủ title + nội dung 1 hàng.
 * Cột thao tác: dùng `tableActionsColDims(iconCount)` (slot × số nút).
 */
import { tableActionsColDims } from './tableActionsColumn'

export const TABLE_LEADING_WIDTHS = {
  /** Nút mở rộng dòng — vừa icon chevron, không thừa */
  expand: 24,
  /** Checkbox chọn dòng — nhỏ nhất vừa icon MUI size=small (p:0) căn giữa */
  checkbox: 24,
  /** STT / No. / 序号 */
  stt: 44,
  /** Ảnh sản phẩm 60×60 sát viền cell (padding 0) */
  image: 60,
} as const

/** Cột mã (SKU, mã khách, mã kho, …) — đủ rộng để hạn chế xuống nhiều dòng */
export const TABLE_COL_CODE = { minWidth: '200px', width: '224px' } as const

/** Cột checkbox — khóa width vừa khít, căn giữa. */
export const TABLE_COL_CHECKBOX = {
  minWidth: `${TABLE_LEADING_WIDTHS.checkbox}px`,
  width: `${TABLE_LEADING_WIDTHS.checkbox}px`,
  maxWidth: `${TABLE_LEADING_WIDTHS.checkbox}px`,
} as const

/** Checkbox trong cột list — khớp cột 24px (size=small + p:0). */
export const TABLE_CHECKBOX_SX = {
  p: 0,
  width: TABLE_LEADING_WIDTHS.checkbox,
  height: TABLE_LEADING_WIDTHS.checkbox,
  display: 'block',
  mx: 'auto',
  '& .MuiSvgIcon-root': { fontSize: 18 },
} as const

/** Keys cột checkbox leading (luôn trước STT trong TableList). */
export const TABLE_LIST_CHECKBOX_KEYS = new Set(['checkbox', 'select'])

export function isTableListCheckboxColumnKey(key: string | number): boolean {
  return TABLE_LIST_CHECKBOX_KEYS.has(String(key))
}

/** Keys cột mở rộng dòng — TableList đặt đầu tiên (trước checkbox / STT). */
export const TABLE_LIST_EXPAND_KEYS = new Set(['expand', 'rowExpand'])

export function isTableListExpandColumnKey(key: string | number): boolean {
  return TABLE_LIST_EXPAND_KEYS.has(String(key))
}

/** Cột nút mở rộng dòng — khóa width nhỏ nhất (chevron). */
export const TABLE_COL_EXPAND = {
  minWidth: `${TABLE_LEADING_WIDTHS.expand}px`,
  width: `${TABLE_LEADING_WIDTHS.expand}px`,
  maxWidth: `${TABLE_LEADING_WIDTHS.expand}px`,
} as const

/** IconButton toggle expand — khớp cột 24px. */
export const TABLE_EXPAND_TOGGLE_SX = {
  p: 0,
  width: TABLE_LEADING_WIDTHS.expand,
  height: TABLE_LEADING_WIDTHS.expand,
  minWidth: TABLE_LEADING_WIDTHS.expand,
  borderRadius: 0.5,
  '& .MuiSvgIcon-root': { fontSize: 18 },
} as const

/** Cột STT — đủ rộng cho header (STT / No. / 序号) trên 1 hàng. */
export const TABLE_COL_STT = {
  minWidth: `${TABLE_LEADING_WIDTHS.stt}px`,
  width: `${TABLE_LEADING_WIDTHS.stt}px`,
  maxWidth: `${TABLE_LEADING_WIDTHS.stt}px`,
} as const

/** Mã ngắn / mã tham chiếu phụ */
export const TABLE_COL_CODE_M = { minWidth: '168px', width: '188px' } as const

/** Mã container / ref */
export const TABLE_COL_REF = { minWidth: '176px', width: '200px' } as const

/** Mã vận đơn / mã đơn dài */
export const TABLE_COL_LADING = { minWidth: '200px', width: '228px' } as const

/**
 * Cột ngày giờ `DD/MM/YYYY | HH:mm` trên 1 hàng — khóa width thống nhất mọi màn
 * (header «Ngày tạo» / «Ngày cập nhật»). Vừa đủ nội dung, không thừa.
 * Meta list: dùng kèm `align: 'left'` (xem `tableListMetaCols`).
 */
export const TABLE_COL_DATETIME = {
  minWidth: '136px',
  width: '136px',
  maxWidth: '136px',
} as const

/** Cột ảnh thumbnail 60×60 — sát viền cell (body padding 0). */
export const TABLE_COL_IMAGE = {
  minWidth: `${TABLE_LEADING_WIDTHS.image}px`,
  width: `${TABLE_LEADING_WIDTHS.image}px`,
  maxWidth: `${TABLE_LEADING_WIDTHS.image}px`,
} as const

/** Khóa cột datetime chuẩn — TableList tự khóa width. */
export const TABLE_LIST_DATETIME_KEYS = new Set([
  'createdAt',
  'updatedAt',
  'created_at',
  'updated_at',
  'ngayTao',
  'ngayCapNhat',
  'ngay_tao',
  'ngay_cap_nhat',
  'ngayTaoList',
  'ngayCapNhatList',
])

/** Khóa cột ảnh thumbnail — TableList khóa width 60px. */
export const TABLE_LIST_IMAGE_KEYS = new Set(['images', 'image', 'anh', 'productImages'])

export function isTableListImageColumnKey(key: string | number): boolean {
  return TABLE_LIST_IMAGE_KEYS.has(String(key))
}

/** Khóa cột thao tác icon — gắn `iconCount` để width = slot × số nút. */
export const TABLE_LIST_ACTIONS_KEYS = new Set(['actions', 'thaoTac', 'action'])

export type TableListColumnSizeInput = {
  key: string
  width?: string
  minWidth?: string
  maxWidth?: string
  /** Số nút icon cột thao tác (xem / sửa / xóa …). */
  iconCount?: number
}

/**
 * Chuẩn hóa width cột cố định (checkbox / STT / ngày / ảnh / thao tác theo số nút).
 * Trả `locked: true` → không giãn khi `fillContainer`.
 */
export function resolveTableListColumnDims(col: TableListColumnSizeInput): {
  width?: string
  minWidth?: string
  maxWidth?: string
  locked: boolean
} {
  const key = String(col.key)

  if (key === 'checkbox' || key === 'select') {
    return { ...TABLE_COL_CHECKBOX, locked: true }
  }
  if (key === 'expand' || key === 'rowExpand') {
    return { ...TABLE_COL_EXPAND, locked: true }
  }
  if (key === 'stt') {
    return { ...TABLE_COL_STT, locked: true }
  }
  if (TABLE_LIST_DATETIME_KEYS.has(key)) {
    return { ...TABLE_COL_DATETIME, locked: true }
  }
  if (isTableListImageColumnKey(key)) {
    return {
      width: col.width ?? TABLE_COL_IMAGE.width,
      minWidth: col.minWidth ?? TABLE_COL_IMAGE.minWidth,
      maxWidth: col.maxWidth ?? TABLE_COL_IMAGE.maxWidth,
      locked: true,
    }
  }
  if (TABLE_LIST_ACTIONS_KEYS.has(key) && col.iconCount != null && col.iconCount > 0) {
    return { ...tableActionsColDims(col.iconCount), locked: true }
  }

  const locked =
    Boolean(col.maxWidth) ||
    col.width != null ||
    TABLE_LIST_ACTIONS_KEYS.has(key) ||
    key === 'onOff' ||
    key === 'onOffAction'

  return {
    width: col.width,
    minWidth: col.minWidth ?? col.width,
    maxWidth: col.maxWidth,
    locked,
  }
}

/**
 * Cột trạng thái chip `ActiveStatusChip` — khóa width vừa khít
 * (header «Trạng thái»; nhãn dài VD «Đang/Ngừng hoạt động»).
 * Meta list: dùng kèm `align: 'center'` (xem `tableListMetaCols`).
 */
export const TABLE_COL_ACTIVE_STATUS = {
  minWidth: '136px',
  width: '136px',
  maxWidth: '136px',
} as const

/**
 * Cột nút `TableListStatusToggleButton` (Bật / Tắt) — khóa width vừa khít.
 * Meta list Hàng hóa: dùng kèm `align: 'center'` (xem `tableListMetaCols`).
 */
export const TABLE_COL_ON_OFF_ACTION = {
  minWidth: '72px',
  width: '72px',
  maxWidth: '72px',
} as const

/** Cột Dừng / Kích hoạt (`StopActivateButton`) — rộng hơn Bật/Tắt. */
export const TABLE_COL_STOP_ACTIVATE_ACTION = {
  minWidth: '108px',
  width: '108px',
  maxWidth: '108px',
} as const

/** Dims cột leading (checkbox / STT) — width = minWidth = maxWidth. */
export function tableLeadingColDims(
  kind: keyof typeof TABLE_LEADING_WIDTHS,
): { width: string; minWidth: string; maxWidth: string } {
  const s = `${TABLE_LEADING_WIDTHS[kind]}px`
  return { width: s, minWidth: s, maxWidth: s }
}
export const tableListStatusToggleButtonSx = {
  minWidth: 0,
  textTransform: 'none' as const,
  height: 28,
  minHeight: 28,
  maxHeight: 28,
  py: 0,
  px: 1,
  borderRadius: '6px',
  fontSize: '0.75rem',
  lineHeight: 1.2,
  boxShadow: 'none',
  '&:hover': { boxShadow: 'none' },
  '& .MuiButton-startIcon': {
    marginRight: 0.35,
    marginLeft: -0.15,
    display: 'inline-flex',
    alignItems: 'center',
    '& .MuiSvgIcon-root': { fontSize: 15 },
  },
} as const

/** Cột wrap / hiện đủ chữ (mặc định TableList đã wrap; giữ export để gắn tường minh). */
export const tableListCellAllowWrapSx = {
  whiteSpace: 'normal !important' as const,
  overflow: 'visible !important' as const,
  textOverflow: 'clip !important' as const,
  wordBreak: 'break-word' as const,
  verticalAlign: 'top' as const,
}

/** Cột nội dung nhiều dòng — gắn vào sx của TableCell tương ứng */
export const ORDER_LIST_WRAP_KEYS = new Set(['totalPack', 'fee', 'customer'])

export const SHIPMENT_LIST_WRAP_KEYS = new Set(['order', 'partner', 'warehouse', 'productName', 'images'])

export const PRODUCT_LIST_WRAP_KEYS = new Set(['name', 'note', 'description', 'businessNote'])

/** Khóa width cột trên body cell (tránh table-layout:fixed bung cột khi minWidth bảng lớn) */
export function tableListFixedColBodySx(col?: {
  width?: string
  minWidth?: string
  maxWidth?: string
}): {
  width?: string
  minWidth?: string
  maxWidth?: string
  boxSizing: 'border-box'
} {
  const w = col?.width ?? col?.minWidth
  if (!w) return { boxSizing: 'border-box' }
  return {
    width: w,
    minWidth: col?.minWidth ?? w,
    maxWidth: col?.maxWidth ?? w,
    boxSizing: 'border-box',
  }
}
